import { useState, type FormEvent } from "react";
import { linkingApi } from "../../api/endpoints.js";
import type { InvitacionNino, Vinculo } from "../../api/types.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Field, TextInput, ErrorBanner, InfoBanner } from "../../components/Form.js";
import { mensajeDeError } from "../../hooks/useQuery.js";
import { useT } from "../../i18n/index.js";

export function LinkChildPage() {
  const t = useT();

  // ---- flujo (a) reclamar código -----------------------------------------
  const [codigo, setCodigo] = useState("");
  const [codigoError, setCodigoError] = useState<string | null>(null);
  const [vinculo, setVinculo] = useState<Vinculo | null>(null);
  const [enviandoCodigo, setEnviandoCodigo] = useState(false);

  async function onReclamar(ev: FormEvent) {
    ev.preventDefault();
    setCodigoError(null);
    setVinculo(null);
    setEnviandoCodigo(true);
    try {
      setVinculo(await linkingApi.reclamarCodigo(codigo.trim()));
      setCodigo("");
    } catch (e) {
      setCodigoError(mensajeDeError(e));
    } finally {
      setEnviandoCodigo(false);
    }
  }

  // ---- flujo (b) invitar a un padre --------------------------------------
  const [nombre, setNombre] = useState("");
  const [fechaNacimiento, setFechaNacimiento] = useState("");
  const [padreEmail, setPadreEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [invitacion, setInvitacion] = useState<InvitacionNino | null>(null);
  const [enviandoInvite, setEnviandoInvite] = useState(false);

  async function onInvitar(ev: FormEvent) {
    ev.preventDefault();
    setInviteError(null);
    setInvitacion(null);
    setEnviandoInvite(true);
    try {
      const res = await linkingApi.invitar({
        nombre: nombre.trim(),
        fechaNacimiento,
        padreEmail: padreEmail.trim(),
      });
      setInvitacion(res);
      setNombre("");
      setFechaNacimiento("");
      setPadreEmail("");
    } catch (e) {
      setInviteError(mensajeDeError(e));
    } finally {
      setEnviandoInvite(false);
    }
  }

  return (
    <>
      <ScreenHeader title={t("therapist.link.title")} back="/terapeuta/pacientes" />
      <Content>
        <Card>
          <h2>{t("therapist.link.claimCode")}</h2>
          <form onSubmit={onReclamar} style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 10 }}>
            {codigoError ? <ErrorBanner message={codigoError} /> : null}
            {vinculo ? (
              <InfoBanner>
                Vínculo creado — estado: <strong>{vinculo.estado}</strong>
              </InfoBanner>
            ) : null}
            <Field label={t("therapist.link.claimCode")}>
              <TextInput
                required
                value={codigo}
                placeholder={t("therapist.link.codePlaceholder")}
                onChange={(e) => setCodigo(e.target.value)}
              />
            </Field>
            <Button type="submit" block disabled={enviandoCodigo || codigo.trim().length === 0}>
              {enviandoCodigo ? t("common.saving") : t("therapist.link.claimCode")}
            </Button>
          </form>
        </Card>

        <Card>
          <h2>{t("therapist.link.invite")}</h2>
          <form onSubmit={onInvitar} style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 10 }}>
            {inviteError ? <ErrorBanner message={inviteError} /> : null}
            {invitacion ? (
              <InfoBanner>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <span>Invitación creada. Comparte este enlace con el padre:</span>
                  <code style={{ wordBreak: "break-all", fontSize: 12 }}>
                    {window.location.origin}/register?invitacion={invitacion.token}
                  </code>
                  <span style={{ fontSize: 12, color: "var(--ink-soft)" }}>Expira: {invitacion.expiraEn}</span>
                </div>
              </InfoBanner>
            ) : null}
            <Field label="Nombre del niño">
              <TextInput required value={nombre} onChange={(e) => setNombre(e.target.value)} />
            </Field>
            <Field label="Fecha de nacimiento">
              <TextInput
                type="date"
                required
                value={fechaNacimiento}
                onChange={(e) => setFechaNacimiento(e.target.value)}
              />
            </Field>
            <Field label={t("therapist.link.parentEmail")}>
              <TextInput
                type="email"
                required
                value={padreEmail}
                onChange={(e) => setPadreEmail(e.target.value)}
              />
            </Field>
            <Button
              type="submit"
              block
              variant="sun"
              disabled={enviandoInvite || !nombre.trim() || !fechaNacimiento || !padreEmail.trim()}
            >
              {enviandoInvite ? t("common.saving") : t("therapist.link.invite")}
            </Button>
          </form>
        </Card>
      </Content>
    </>
  );
}
